import { computed, inject, Injectable, signal } from '@angular/core';
import { LegacyPreferenceStorageService } from '../realtime/legacy-preference-storage.service';

const VALID_DRIVER_STATUS_IDS = new Set([0, 1, 2, 10]);
const VALID_HEATMAP_IDS = new Set([0, 1, 2, 3, 4]);
const MAP_STATUS_STORAGE_KEY = 'monitoring_map_status_v2';
const HEATMAP_STORAGE_KEY = 'heatmap';
const PREDICTIONS_STORAGE_KEY = 'surge_heatmap';
const ONLINE_USERS_STORAGE_KEY = 'users_heatmap';
const OFFLINE_DRIVERS_STORAGE_KEY = 'offline_partners';
const MAP_REGION_STORAGE_KEY = 'monitoring_map_region_ids_v1';

@Injectable({ providedIn: 'root' })
export class MapOverlayPreferencesService {
  private readonly storage = inject(LegacyPreferenceStorageService);
  private readonly selectedStatusIdState = signal(
    normalizeStoredNumber(this.storage.readNumber(MAP_STATUS_STORAGE_KEY, 10), VALID_DRIVER_STATUS_IDS, 10)
  );
  private readonly selectedHeatmapIdState = signal(
    normalizeStoredNumber(this.storage.readNumber(HEATMAP_STORAGE_KEY, 0), VALID_HEATMAP_IDS, 0)
  );
  private readonly showPredictionsState = signal(this.storage.readBoolean(PREDICTIONS_STORAGE_KEY, false));
  private readonly showOnlineUsersState = signal(this.storage.readBoolean(ONLINE_USERS_STORAGE_KEY, false));
  private readonly showOfflineDriversState = signal(
    this.storage.readBoolean(OFFLINE_DRIVERS_STORAGE_KEY, false)
  );

  // Regions picked in the map's own Regions dropdown. Shared so the header driver counts follow the same
  // selection as the markers on the map. An empty list (or the "no filter" id) means all regions.
  private readonly selectedRegionIdsState = signal<number[]>(this.storage.readJson<number[]>(MAP_REGION_STORAGE_KEY, []));

  readonly selectedRegionIds = this.selectedRegionIdsState.asReadonly();
  readonly selectedStatusId = this.selectedStatusIdState.asReadonly();
  readonly selectedHeatmapId = this.selectedHeatmapIdState.asReadonly();
  readonly showPredictions = this.showPredictionsState.asReadonly();
  readonly showOnlineUsers = this.showOnlineUsersState.asReadonly();
  readonly showOfflineDrivers = this.showOfflineDriversState.asReadonly();
  readonly overlayState = computed(() => ({
    selectedStatusId: this.selectedStatusId(),
    selectedHeatmapId: this.selectedHeatmapId(),
    showPredictions: this.showPredictions(),
    showOnlineUsers: this.showOnlineUsers(),
    showOfflineDrivers: this.showOfflineDrivers()
  }));

  setSelectedRegionIds(regionIds: number[]): void {
    this.selectedRegionIdsState.set(regionIds);
    this.storage.writeJson(MAP_REGION_STORAGE_KEY, regionIds);
  }

  setSelectedStatusId(statusId: number): void {
    const normalizedStatusId = normalizeStoredNumber(statusId, VALID_DRIVER_STATUS_IDS, 10);
    this.selectedStatusIdState.set(normalizedStatusId);
    this.storage.writeString(MAP_STATUS_STORAGE_KEY, `${normalizedStatusId}`);
  }

  setSelectedHeatmapId(heatmapId: number): void {
    const normalizedHeatmapId = normalizeStoredNumber(heatmapId, VALID_HEATMAP_IDS, 0);
    this.selectedHeatmapIdState.set(normalizedHeatmapId);
    this.storage.writeString(HEATMAP_STORAGE_KEY, `${normalizedHeatmapId}`);
  }

  setShowPredictions(enabled: boolean): void {
    this.showPredictionsState.set(enabled);
    this.storage.writeBoolean(PREDICTIONS_STORAGE_KEY, enabled);
  }

  setShowOnlineUsers(enabled: boolean): void {
    this.showOnlineUsersState.set(enabled);
    this.storage.writeBoolean(ONLINE_USERS_STORAGE_KEY, enabled);
  }

  setShowOfflineDrivers(enabled: boolean): void {
    this.showOfflineDriversState.set(enabled);
    this.storage.writeBoolean(OFFLINE_DRIVERS_STORAGE_KEY, enabled);
  }
}

function normalizeStoredNumber(value: number, validValues: Set<number>, fallback: number): number {
  return validValues.has(value) ? value : fallback;
}
